import React, { useState, useEffect } from "react";
import OfferForm from "./OfferForm";
import OfferTable from "./OfferTable";
import CreatableSelect from 'react-select/creatable';
import axios from "axios";
import api from "../utils/api";
import "../Admin.css";

const ProductManager = () => {
    const [editingOffer, setEditingOffer] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importUrl, setImportUrl] = useState("");
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState(null);
    const [showShopeeModal, setShowShopeeModal] = useState(false);
    const [shopeeSearchTerm, setShopeeSearchTerm] = useState("");
    const [searchingShopee, setSearchingShopee] = useState(false);
    const [shopeeStatus, setShopeeStatus] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("Eletrônicos");
    const [selectedSubcategory, setSelectedSubcategory] = useState("");

    // Estados para Importação em Lote
    const [showBulkImportModal, setShowBulkImportModal] = useState(false);
    const [bulkUrls, setBulkUrls] = useState("");
    const [bulkImporting, setBulkImporting] = useState(false);
    const [bulkImportResult, setBulkImportResult] = useState(null);
    const [bulkImportError, setBulkImportError] = useState(null);
    const [bulkCategory, setBulkCategory] = useState("Eletrônicos");
    const [bulkSubcategory, setBulkSubcategory] = useState("");
    const [bulkSubcategories, setBulkSubcategories] = useState([]);

    // Atualizar subcategorias do lote quando a categoria muda
    useEffect(() => {
        if (bulkCategory) {
            const cat = categories.find(c => c.name === bulkCategory);
            setBulkSubcategories(cat?.subcategories || []);
        } else {
            setBulkSubcategories([]);
        }
    }, [bulkCategory, categories]);

    // Buscar categorias ao montar o componente
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/categorias');
                setCategories(response.data);
            } catch (error) {
                console.error("Erro ao buscar categorias:", error);
            }
        };
        fetchCategories();
    }, []);

    // Atualizar subcategorias quando a categoria muda
    useEffect(() => {
        if (selectedCategory) {
            const cat = categories.find(c => c.name === selectedCategory);
            setSubcategories(cat?.subcategories || []);
        } else {
            setSubcategories([]);
        }
    }, [selectedCategory, categories]);

    const handleEditClick = (offer) => {
        setEditingOffer(offer);
    };

    const handleSuccess = () => {
        setRefreshKey((prevKey) => prevKey + 1);
        setEditingOffer(null);
    };

    const handleCancelEdit = () => {
        setEditingOffer(null);
    };

    const handleShopeeSearch = async () => {
        if (!shopeeSearchTerm.trim()) {
            setShopeeStatus({ type: 'error', msg: "Por favor, digite um produto ou link." });
            return;
        }

        setSearchingShopee(true);
        setShopeeStatus({ type: 'info', msg: "Iniciando busca na Shopee via API Oficial..." });

        try {
            // Agora chama o BACKEND diretamente (que gerencia a assinatura e a API da Shopee)
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/shopee/sync`, {
                keyword: shopeeSearchTerm,
                categoria: selectedCategory,
                subcategoria: selectedSubcategory
            });

            if (response.data.success) {
                const novos = response.data.novosCadastros;
                setShopeeStatus({ 
                    type: 'success', 
                    msg: novos > 0 
                        ? `${novos} novos produtos cadastrados com sucesso!` 
                        : "Nenhum produto novo encontrado (podem já estar cadastrados)." 
                });
                
                setTimeout(() => {
                    setShowShopeeModal(false);
                    setShopeeSearchTerm("");
                    setShopeeStatus(null);
                    setRefreshKey(prev => prev + 1); // Atualiza a lista na tela
                }, 3000);
            }
        } catch (error) {
            console.error("Erro ao buscar na Shopee:", error);
            setShopeeStatus({ 
                type: 'error', 
                msg: error.response?.data?.message || "Erro ao conectar com o serviço de busca." 
            });
        } finally {
            setSearchingShopee(false);
        }
    };

    const handleImport = async () => {
        if (!importUrl.trim()) {
            setImportError("Por favor, insira uma URL válida.");
            return;
        }

        setImporting(true);
        setImportError(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/affiliate/import`, {
                url: importUrl
            });

            if (response.data.success) {
                const importedData = response.data.data;
                const newOffer = {
                    titulo: importedData.titulo,
                    descricao: importedData.descricao,
                    preco_original: "",
                    preco_desconto: "",
                    imagem_url: importedData.imagem_url,
                    loja: importedData.loja,
                    link_afiliado: importedData.link_afiliado,
                    status: "ATIVA",
                    categoria: "Eletrônicos",
                    subcategoria: ""
                };
                setEditingOffer(newOffer);
                setShowImportModal(false);
                setImportUrl("");
            }
        } catch (error) {
            console.error("Erro ao importar:", error);
            setImportError(error.response?.data?.error || "Erro ao importar oferta.");
        } finally {
            setImporting(false);
        }
    };

    const handleBulkImport = async () => {
        if (!bulkUrls.trim()) {
            setBulkImportError("Por favor, insira pelo menos uma URL.");
            return;
        }

        setBulkImporting(true);
        setBulkImportError(null);
        setBulkImportResult(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/affiliate/import-bulk`, {
                urls: bulkUrls,
                categoria: bulkCategory,
                subcategoria: bulkSubcategory
            });

            if (response.data.success) {
                setBulkImportResult(response.data.results);
                setRefreshKey(prev => prev + 1); // Atualizar tabela de ofertas
            }
        } catch (error) {
            console.error("Erro ao importar em lote:", error);
            setBulkImportError(error.response?.data?.error || "Erro ao processar lote.");
        } finally {
            setBulkImporting(false);
        }
    };

    return (
        <>
            <div className="page-header">
                <h2>Gerenciamento de Produtos</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn-secondary" style={{ backgroundColor: '#ff4742', color: 'white', border: 'none' }} onClick={() => setShowShopeeModal(true)}>
                        <i className="fas fa-shopping-bag"></i> Buscar na Shopee
                    </button>
                    <button className="btn-secondary" style={{ backgroundColor: '#00a650', color: 'white', border: 'none' }} onClick={() => {
                        setShowBulkImportModal(true);
                        setBulkUrls("");
                        setBulkImportResult(null);
                        setBulkImportError(null);
                    }}>
                        <i className="fas fa-cubes"></i> Importar em Lote
                    </button>
                    <button className="btn-secondary" onClick={() => setShowImportModal(true)}>
                        <i className="fas fa-download"></i> Importar de Link
                    </button>
                    <button className="btn-primary" onClick={() => setEditingOffer({})}>
                        <i className="fas fa-plus"></i> Nova Oferta
                    </button>
                </div>
            </div>

            {/* Shopee Search Modal */}
            {showShopeeModal && (
                <div className="modal-overlay" onClick={() => setShowShopeeModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Buscar Produtos na Shopee</h3>
                            <button className="modal-close" onClick={() => setShowShopeeModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '15px', color: '#666' }}>
                                Digite o nome do produto ou link. O n8n irá buscar e cadastrar automaticamente.
                            </p>
                            <div className="form-group">
                                <label>Produto ou Link Shopee</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ex: Xiaomi Poco X6 ou link do produto"
                                    value={shopeeSearchTerm}
                                    onChange={(e) => setShopeeSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleShopeeSearch()}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                <div className="form-group">
                                    <label>Categoria Principal</label>
                                    <CreatableSelect
                                        options={categories.map(c => ({ value: c.name, label: c.name }))}
                                        value={selectedCategory ? { value: selectedCategory, label: selectedCategory } : null}
                                        onChange={(opt) => {
                                            setSelectedCategory(opt?.value || "");
                                            setSelectedSubcategory("");
                                        }}
                                        placeholder="Selecione..."
                                        isClearable
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Subcategoria</label>
                                    <CreatableSelect
                                        options={subcategories.map(s => ({ value: s.name, label: s.name }))}
                                        value={selectedSubcategory ? { value: selectedSubcategory, label: selectedSubcategory } : null}
                                        onChange={(opt) => setSelectedSubcategory(opt?.value || "")}
                                        placeholder="Opcional..."
                                        isClearable
                                        isDisabled={!selectedCategory}
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                            </div>
                            {shopeeStatus && (
                                <div className={`alert alert-${shopeeStatus.type === 'error' ? 'danger' : shopeeStatus.type === 'info' ? 'info' : 'success'}`}>
                                    {shopeeStatus.msg}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowShopeeModal(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleShopeeSearch} disabled={searchingShopee}>
                                {searchingShopee ? 'Buscando...' : 'Iniciar Busca'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Importar Oferta de Link</h3>
                            <button className="modal-close" onClick={() => setShowImportModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '15px', color: '#666' }}>
                                Cole o link do produto abaixo. Vamos importar título, descrição e imagem.
                            </p>
                            <div className="form-group">
                                <label>URL do Produto</label>
                                <input
                                    type="url"
                                    className="form-input"
                                    placeholder="https://www.amazon.com.br/..."
                                    value={importUrl}
                                    onChange={(e) => setImportUrl(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleImport()}
                                />
                            </div>
                            {importError && <div className="alert alert-danger">{importError}</div>}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowImportModal(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleImport} disabled={importing}>
                                {importing ? 'Importando...' : 'Importar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Import Modal */}
            {showBulkImportModal && (
                <div className="modal-overlay" onClick={() => !bulkImporting && setShowBulkImportModal(false)}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h3>Importar Produtos em Lote (Remessa)</h3>
                            <button className="modal-close" onClick={() => !bulkImporting && setShowBulkImportModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '15px', color: '#666' }}>
                                Insira múltiplos links de produtos (Lomadee, PromoFarma, Amazon, Shopee, Mercado Livre, etc), um por linha. 
                                O sistema irá buscar a foto, título, descrição e preços automaticamente de cada um e cadastrá-los na plataforma.
                            </p>
                            
                            <div className="form-group">
                                <label>URLs dos Produtos (Uma por linha - Máx 20 por envio)</label>
                                <textarea
                                    className="form-input"
                                    rows={8}
                                    placeholder="https://www.mercadolivre.com.br/produto/...&#10;https://www.amazon.com.br/...&#10;https://shope.ee/..."
                                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px', resize: 'vertical' }}
                                    value={bulkUrls}
                                    onChange={(e) => setBulkUrls(e.target.value)}
                                    disabled={bulkImporting}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                <div className="form-group">
                                    <label>Categoria Principal</label>
                                    <CreatableSelect
                                        options={categories.map(c => ({ value: c.name, label: c.name }))}
                                        value={bulkCategory ? { value: bulkCategory, label: bulkCategory } : null}
                                        onChange={(opt) => {
                                            setBulkCategory(opt?.value || "");
                                            setBulkSubcategory("");
                                        }}
                                        placeholder="Selecione ou crie..."
                                        isClearable
                                        disabled={bulkImporting}
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Subcategoria</label>
                                    <CreatableSelect
                                        options={bulkSubcategories.map(s => ({ value: s.name, label: s.name }))}
                                        value={bulkSubcategory ? { value: bulkSubcategory, label: bulkSubcategory } : null}
                                        onChange={(opt) => setBulkSubcategory(opt?.value || "")}
                                        placeholder="Opcional..."
                                        isClearable
                                        isDisabled={!bulkCategory || bulkImporting}
                                        formatCreateLabel={(v) => `Criar "${v}"`}
                                    />
                                </div>
                            </div>

                            {bulkImportError && (
                                <div className="alert alert-danger" style={{ marginTop: '15px' }}>
                                    {bulkImportError}
                                </div>
                            )}

                            {bulkImportResult && (
                                <div className="alert alert-success" style={{ marginTop: '15px', color: '#155724', backgroundColor: '#d4edda', borderColor: '#c3e6cb', padding: '15px', borderRadius: '5px' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Resultado do Processamento:</h4>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li><strong>Novos cadastrados:</strong> {bulkImportResult.successCount}</li>
                                        <li><strong>Existentes atualizados:</strong> {bulkImportResult.updatedCount}</li>
                                        <li><strong>Falhas:</strong> {bulkImportResult.failedCount}</li>
                                    </ul>
                                    {bulkImportResult.items && bulkImportResult.items.length > 0 && (
                                        <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto', background: 'rgba(255,255,255,0.6)', padding: '10px', borderRadius: '4px' }}>
                                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '12px' }}>Produtos Importados:</p>
                                            {bulkImportResult.items.map((item, idx) => (
                                                <div key={idx} style={{ fontSize: '12px', display: 'flex', gap: '8px', borderBottom: '1px solid #ddd', padding: '4px 0' }}>
                                                    <span style={{ color: item.status === 'created' ? '#28a745' : '#007bff', fontWeight: 'bold' }}>
                                                        [{item.status === 'created' ? 'NOVO' : 'ATUALIZADO'}]
                                                    </span>
                                                    <span>{item.titulo} ({item.loja})</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {bulkImportResult.errors && bulkImportResult.errors.length > 0 && (
                                        <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto', background: 'rgba(220,53,69,0.1)', padding: '10px', borderRadius: '4px' }}>
                                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '12px', color: '#dc3545' }}>Falhas de Importação:</p>
                                            {bulkImportResult.errors.map((err, idx) => (
                                                <div key={idx} style={{ fontSize: '12px', borderBottom: '1px solid #f5c6cb', padding: '4px 0' }}>
                                                    <div style={{ wordBreak: 'break-all', fontWeight: '500' }}>Link: {err.url}</div>
                                                    <div style={{ color: '#dc3545', fontStyle: 'italic' }}>Erro: {err.error}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowBulkImportModal(false)} disabled={bulkImporting}>
                                {bulkImportResult ? 'Fechar' : 'Cancelar'}
                            </button>
                            <button className="btn-primary" onClick={handleBulkImport} disabled={bulkImporting || !bulkUrls.trim()}>
                                {bulkImporting ? 'Processando Lote...' : 'Processar e Lançar Lote'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Offer Edit Modal */}
            {editingOffer && (
                <div className="modal-overlay" onClick={handleCancelEdit}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h3>{editingOffer.id ? 'Editar Oferta' : 'Nova Oferta'}</h3>
                            <button className="modal-close" onClick={handleCancelEdit}>×</button>
                        </div>
                        <div className="modal-body">
                            <OfferForm
                                editingOffer={editingOffer.id ? editingOffer : null}
                                importedData={!editingOffer.id ? editingOffer : null}
                                onSuccess={handleSuccess}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="card-container">
                <div className="card-header">
                    <h3>Lista de Produtos</h3>
                </div>
                <OfferTable onEditClick={handleEditClick} refreshKey={refreshKey} />
            </div>
        </>
    );
};

export default ProductManager;
